import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

import Select from '../../../components/ui/Select';

const BeforeAfterComparison = ({ treatmentId, data, onUpdate }) => {
  const [comparison, setComparison] = useState({
    beforePhotos: data?.beforePhotos || [],
    afterPhotos: data?.afterPhotos || [],
    symptomImprovement: data?.symptomImprovement || null,
    visualChanges: data?.visualChanges || '',
    functionalChanges: data?.functionalChanges || '',
    shareWithProvider: data?.shareWithProvider !== false
  });

  useEffect(() => {
    onUpdate(comparison);
  }, [comparison]);

  const handleComparisonChange = (field, value) => {
    setComparison(prev => ({ ...prev, [field]: value }));
  };

  const handlePhotoUpload = (type, event) => {
    const files = Array?.from(event?.target?.files || []);
    const newPhotos = files?.map(file => ({
      id: `photo-${Date?.now()}-${Math?.random()}`,
      file: file,
      preview: URL?.createObjectURL(file),
      uploadedAt: new Date()?.toISOString()
    }));

    if (type === 'before') {
      setComparison(prev => ({
        ...prev,
        beforePhotos: [...prev?.beforePhotos, ...newPhotos]
      }));
    } else {
      setComparison(prev => ({
        ...prev,
        afterPhotos: [...prev?.afterPhotos, ...newPhotos]
      }));
    }
  };

  const handlePhotoRemove = (type, photoId) => {
    if (type === 'before') {
      setComparison(prev => ({
        ...prev,
        beforePhotos: prev?.beforePhotos?.filter(p => p?.id !== photoId)
      }));
    } else {
      setComparison(prev => ({
        ...prev,
        afterPhotos: prev?.afterPhotos?.filter(p => p?.id !== photoId)
      }));
    }
  };

  const improvementOptions = [
    { value: 'excellent', label: 'Excellent - Exceeded Expectations' },
    { value: 'very-good', label: 'Very Good - Better Than Expected' },
    { value: 'good', label: 'Good - Met Expectations' },
    { value: 'fair', label: 'Fair - Some Improvement' },
    { value: 'poor', label: 'Poor - Minimal Improvement' },
    { value: 'no-improvement', label: 'No Improvement' }
  ];

  const PhotoUploadSection = ({ type, photos, onUpload, onRemove }) => {
    const title = type === 'before' ? 'Before Treatment' : 'After Treatment';
    const description = type === 'before' ?'Upload photos showing your condition before treatment' :'Upload current photos showing treatment results';

    return (
      <div className="space-y-4">
        <div>
          <h4 className="text-sm font-semibold text-foreground mb-1">{title}</h4>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {photos?.map(photo => (
            <div key={photo?.id} className="relative group">
              <img
                src={photo?.preview}
                alt={`${title} photo`}
                className="w-full h-32 object-cover rounded-lg border border-border"
              />
              <button
                type="button"
                onClick={() => onRemove(photo?.id)}
                className="absolute top-2 right-2 bg-error text-error-foreground rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Icon name="X" size={16} />
              </button>
            </div>
          ))}

          <label className="flex flex-col items-center justify-center h-32 border-2 border-dashed border-border rounded-lg cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors">
            <Icon name="Upload" size={24} className="text-muted-foreground mb-2" />
            <span className="text-xs text-muted-foreground">Upload Photo</span>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => onUpload(e)}
              className="hidden"
            />
          </label>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-2 flex items-center gap-2">
          <Icon name="ImageIcon" size={20} className="text-primary" />
          Visual Documentation
        </h3>
        <p className="text-sm text-muted-foreground mb-6">
          Upload photos to document your treatment progress (optional but helpful)
        </p>

        <div className="bg-primary/5 rounded-lg border border-primary/20 p-4 mb-6">
          <div className="flex items-start gap-3">
            <Icon name="Shield" size={20} className="text-primary flex-shrink-0 mt-0.5" />
            <div className="text-sm text-muted-foreground">
              <p className="font-medium text-foreground mb-1">Privacy & Security</p>
              <p>All photos are encrypted and stored securely in compliance with HIPAA regulations. Only authorized healthcare providers will have access to your images.</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <PhotoUploadSection
            type="before"
            photos={comparison?.beforePhotos}
            onUpload={(e) => handlePhotoUpload('before', e)}
            onRemove={(id) => handlePhotoRemove('before', id)}
          />
          <PhotoUploadSection
            type="after"
            photos={comparison?.afterPhotos}
            onUpload={(e) => handlePhotoUpload('after', e)}
            onRemove={(id) => handlePhotoRemove('after', id)}
          />
        </div>
      </div>

      {comparison?.beforePhotos?.length > 0 && comparison?.afterPhotos?.length > 0 && (
        <div className="border-t border-border pt-6">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="Maximize2" size={20} className="text-primary" />
            Side-by-Side Comparison
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-2">Before Treatment</p>
              <img
                src={comparison?.beforePhotos?.[0]?.preview}
                alt="Before treatment"
                className="w-full h-64 object-cover rounded-lg border border-border"
              />
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-2">After Treatment</p>
              <img
                src={comparison?.afterPhotos?.[0]?.preview}
                alt="After treatment"
                className="w-full h-64 object-cover rounded-lg border border-border"
              />
            </div>
          </div>
        </div>
      )}

      <div className="border-t border-border pt-6">
        <h3 className="text-lg font-semibold text-foreground mb-6 flex items-center gap-2">
          <Icon name="TrendingUp" size={20} className="text-primary" />
          Overall Improvement Assessment
        </h3>
        <Select
          label="How would you rate your overall improvement?"
          placeholder="Select improvement level"
          options={improvementOptions}
          value={comparison?.symptomImprovement}
          onChange={(value) => handleComparisonChange('symptomImprovement', value)}
        />
      </div>

      <div className="border-t border-border pt-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Eye" size={20} className="text-primary" />
          Visual Changes
        </h3>
        <textarea
          value={comparison?.visualChanges}
          onChange={(e) => handleComparisonChange('visualChanges', e?.target?.value)}
          placeholder="Describe any visual changes you've noticed (e.g., color, shape, alignment, swelling reduction)..."
          rows={4}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        />
      </div>

      <div className="border-t border-border pt-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Activity" size={20} className="text-primary" />
          Functional Changes
        </h3>
        <textarea
          value={comparison?.functionalChanges}
          onChange={(e) => handleComparisonChange('functionalChanges', e?.target?.value)}
          placeholder="Describe any functional improvements (e.g., easier chewing, reduced pain, better speech, improved confidence)..."
          rows={4}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        />
      </div>

      <div className="border-t border-border pt-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Share2" size={20} className="text-primary" />
          Sharing Preferences
        </h3>
        <label className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg border border-border cursor-pointer hover:bg-muted transition-colors">
          <input
            type="checkbox"
            checked={comparison?.shareWithProvider}
            onChange={(e) => handleComparisonChange('shareWithProvider', e?.target?.checked)}
            className="mt-1 h-4 w-4 rounded border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
          />
          <div>
            <p className="text-sm font-medium text-foreground mb-1">
              Share photos with my healthcare provider
            </p>
            <p className="text-xs text-muted-foreground">
              Allow your provider to view these photos for clinical documentation and follow-up care planning
            </p>
          </div>
        </label>
      </div>

      <div className="bg-success/10 rounded-lg border border-success/20 p-4">
        <div className="flex items-start gap-3">
          <Icon name="Camera" size={20} className="text-success flex-shrink-0 mt-0.5" />
          <div className="text-sm text-muted-foreground">
            <p className="font-medium text-foreground mb-1">Photography Tips</p>
            <ul className="space-y-1 text-xs">
              <li>• Use good lighting and take photos from the same angle</li>
              <li>• Include close-up and wider views of the treatment area</li>
              <li>• Take photos in a well-lit room or natural daylight</li>
              <li>• Ensure the treatment area is clearly visible</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BeforeAfterComparison;